import * as RadixSwitch from '@radix-ui/react-switch';
import { forwardRef } from 'react';

import { Label } from '~/components/granular';
import { classNames } from '~/utils/styles';

import type { SwitchProps } from './Switch.types';

export const Switch = forwardRef<HTMLDivElement, SwitchProps>(
  (
    {
      className,
      id,
      checked,
      disabled,
      label,
      labelPosition = 'right',
      description,
      appearance = 'light',
      size = 'medium',
      ...props
    },
    forwardedRef,
  ) => {
    const background = disabled ? 'bg-neutral-400' : checked ? 'bg-teal-700' : 'bg-neutral-600';
    const isSmall = size === 'small';
    const animation = classNames(
      'translate-x-0.5 transition-transform duration-100 will-change-transform',
      isSmall ? 'data-[state=checked]:translate-x-[14px]' : 'data-[state=checked]:translate-x-[16px]',
    );
    const descriptionColor = disabled ? 'text-neutral-500' : 'text-neutral-800';

    // The label only activates the switch when it is associated with it via an id.
    const labelElement = (label || description) && (
      <div className="flex flex-col">
        <Label
          appearance={appearance}
          className={classNames(
            'text-nowrap',
            labelPosition === 'left' ? 'mr-1.5' : 'ml-1.5',
            id ? 'cursor-pointer' : 'cursor-default',
            {
              'text-[13px] leading-[18px]': isSmall,
            },
          )}
          disabled={disabled}
          htmlFor={id}
          position={labelPosition}
        >
          {label}
        </Label>
        {description && (
          <div
            className={classNames(
              'body-300-light mt-0.5',
              labelPosition === 'left' ? 'mr-1.5' : 'ml-1.5',
              descriptionColor,
            )}
          >
            {description}
          </div>
        )}
      </div>
    );

    return (
      <div className={classNames('flex', className)} ref={forwardedRef}>
        {labelPosition === 'left' && labelElement}

        <RadixSwitch.Root
          checked={checked}
          className={classNames('my-px shrink-0 rounded-full', isSmall ? 'h-4 w-7' : 'h-[18px] w-8', background)}
          disabled={disabled}
          id={id}
          {...props}
        >
          <RadixSwitch.Thumb
            className={classNames(
              'my-0.5 block rounded-full bg-white data-disabled:bg-neutral-100',
              isSmall ? 'h-3 w-3' : 'h-[14px] w-[14px]',
              animation,
            )}
          />
        </RadixSwitch.Root>

        {labelPosition === 'right' && labelElement}
      </div>
    );
  },
);
